/**
 * RunHistoryView — Profiler run history & comparison (FR105-FR109).
 *
 * Lists every persisted run for the current project (or — when the page is
 * loaded outside a project — the current session) with date, source, score,
 * and per-run actions. From here the user can:
 *
 *   - Pick two runs and view a side-by-side diff (score delta, schema
 *     changes, new/resolved/persistent issues, table add/remove).
 *   - Open a single historical run to inspect its full snapshot.
 *
 * The diff calculation lives on the backend (RunComparator); this component
 * is purely a presenter.
 */

import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2, RefreshCcw, Scale, X } from "lucide-react";
import {
  profilerApi,
  type RunComparisonResponse,
  type RunHistorySummary,
} from "../../api/profiler-api";
import { RunOptionsBadges } from "./RunOptionsBadge";

interface RunHistoryViewProps {
  /** Project context; preferred filter when present (cross-session history). */
  projectId?: string;
  /** Fall-back filter when no project is bound (single-session history). */
  sessionId?: string;
}

export function RunHistoryView({ projectId, sessionId }: RunHistoryViewProps) {
  const [runs, setRuns] = useState<RunHistorySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compare picker state — at most two run_ids selected at any time.
  const [picked, setPicked] = useState<string[]>([]);
  const [comparison, setComparison] = useState<RunComparisonResponse | null>(
    null,
  );
  const [comparing, setComparing] = useState(false);
  const [compareErr, setCompareErr] = useState<string | null>(null);

  const canFetch = !!projectId || !!sessionId;

  const reload = async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    try {
      const res = await profilerApi.listRuns({
        project_id: projectId || undefined,
        session_id: !projectId ? sessionId : undefined,
        limit: 50,
      });
      setRuns(res.runs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId, sessionId]);

  const togglePick = (runId: string) => {
    setPicked((prev) => {
      if (prev.includes(runId)) return prev.filter((p) => p !== runId);
      // Cap at 2 — drop the oldest pick when a third is added so the UX
      // stays a "pick two and compare" rather than a multi-select.
      const next = [...prev, runId];
      return next.length > 2 ? next.slice(-2) : next;
    });
  };

  const runCompare = async () => {
    if (picked.length !== 2) return;
    setComparing(true);
    setCompareErr(null);
    try {
      // Order: older → newer. The API treats the first arg as the baseline.
      const [a, b] = picked.map((id) => runs.find((r) => r.run_id === id)!);
      const [base, compare] =
        new Date(a.completed_at) <= new Date(b.completed_at) ? [a, b] : [b, a];
      const res = await profilerApi.compareRuns(base.run_id, compare.run_id);
      setComparison(res);
    } catch (e) {
      setCompareErr(e instanceof Error ? e.message : String(e));
    } finally {
      setComparing(false);
    }
  };

  if (!canFetch) {
    return (
      <div className="glass-surface rounded-xl p-8 text-center">
        <p className="text-body text-[var(--muted-foreground)]">
          Run history requires a project context. Open the profiler from a
          project to track snapshots across runs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl">
      {/* Header */}
      <div className="flex items-baseline gap-3">
        <h3 className="text-lg font-semibold text-[var(--foreground)]">
          Run History
        </h3>
        <span className="text-sm text-[var(--muted-foreground)]">
          {runs.length} {runs.length === 1 ? "run" : "runs"} stored
          {projectId ? " for this project" : " for this session"}
        </span>
        <button
          onClick={() => void reload()}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-50"
          title="Refresh run history"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCcw className="w-3.5 h-3.5" />
          )}
          Refresh
        </button>
      </div>

      {/* Compare action bar — appears once 2 runs are picked. */}
      {picked.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/10 px-3 py-2">
          <Scale className="w-4 h-4 text-[var(--primary)]" />
          <span className="text-xs text-[var(--foreground)]">
            {picked.length === 1
              ? "Pick one more run to compare."
              : "Two runs selected. Compare to see DQ delta, schema changes, and resolved issues."}
          </span>
          {picked.length === 2 && (
            <button
              onClick={() => void runCompare()}
              disabled={comparing}
              className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-md bg-[var(--primary)] text-black font-medium hover:bg-[var(--primary)]/90 disabled:opacity-50"
            >
              {comparing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ArrowRight className="w-3.5 h-3.5" />
              )}
              Compare
            </button>
          )}
          <button
            onClick={() => setPicked([])}
            className="text-xs text-white/55 hover:text-white px-2 py-1 rounded-md hover:bg-white/5"
            title="Clear selection"
          >
            Clear
          </button>
        </div>
      )}

      {/* Runs table */}
      {error && (
        <div className="rounded-md border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {loading && runs.length === 0 ? (
        <div className="glass-surface rounded-xl p-8 text-center text-sm text-white/60">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
          Loading run history…
        </div>
      ) : runs.length === 0 ? (
        <div className="glass-surface rounded-xl p-8 text-center text-sm text-white/60">
          No profiling runs have been recorded yet. Once you complete a
          profiling pass, the snapshot will appear here.
        </div>
      ) : (
        <div
          className="glass-surface rounded-xl overflow-hidden border border-white/10"
          data-testid="run-history-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-white/55">
              <tr>
                <th className="text-left px-3 py-2 w-10" aria-label="Select" />
                <th className="text-left px-3 py-2">Completed</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-right px-3 py-2">Score</th>
                <th className="text-right px-3 py-2">Tables</th>
                <th className="text-right px-3 py-2">Elapsed</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <RunRow
                  key={r.run_id}
                  run={r}
                  selected={picked.includes(r.run_id)}
                  onToggle={() => togglePick(r.run_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Compare result panel — full-width inline panel (no modal needed). */}
      {compareErr && (
        <div className="rounded-md border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">
          Compare failed: {compareErr}
        </div>
      )}
      {comparison && (
        <CompareResultPanel
          comparison={comparison}
          onClose={() => setComparison(null)}
        />
      )}
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────

export function RunRow({
  run,
  selected,
  onToggle,
}: {
  run: RunHistorySummary;
  selected: boolean;
  onToggle: () => void;
}) {
  const completedDate = run.completed_at ? new Date(run.completed_at) : null;
  const scoreColor =
    run.aggregate_score >= 90
      ? "text-emerald-300"
      : run.aggregate_score >= 75
        ? "text-amber-200"
        : "text-red-300";

  return (
    <tr
      className={`border-t border-white/5 transition-colors ${
        selected ? "bg-[var(--primary)]/10" : "hover:bg-white/[0.02]"
      }`}
    >
      <td className="px-3 py-2.5 align-middle">
        <input
          type="checkbox"
          aria-label={`Select run completed ${run.completed_at}`}
          checked={selected}
          onChange={onToggle}
          className="cursor-pointer"
        />
      </td>
      <td className="px-3 py-2.5 align-middle">
        <div className="text-white/85">
          {completedDate ? completedDate.toLocaleString() : "—"}
        </div>
        <div className="text-[11px] text-white/40 font-mono">{run.run_id}</div>
      </td>
      <td className="px-3 py-2.5 align-middle">
        <div className="text-white/85">{run.source_name || "—"}</div>
        <div className="text-[11px] text-white/40">{run.source_type}</div>
      </td>
      <td className="px-3 py-2.5 align-middle text-right">
        <div className={`font-semibold tabular-nums ${scoreColor}`}>
          {Math.round(run.aggregate_score)}
        </div>
        <RunOptionsBadges
          options={run.run_options}
          className="justify-end mt-1 max-w-[14rem] ml-auto"
        />
      </td>
      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-white/75">
        {run.tables_profiled}
        {run.tables_total > 0 && run.tables_total !== run.tables_profiled && (
          <span className="text-white/40">/{run.tables_total}</span>
        )}
      </td>
      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-white/60">
        {run.elapsed_seconds > 0 ? `${run.elapsed_seconds.toFixed(1)}s` : "—"}
      </td>
      <td className="px-3 py-2.5 align-middle">
        <span
          className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
            run.status === "completed"
              ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/30"
              : run.status === "failed"
                ? "bg-red-400/10 text-red-300 border-red-400/30"
                : "bg-white/5 text-white/60 border-white/10"
          }`}
        >
          {run.status}
        </span>
      </td>
    </tr>
  );
}

// ── Compare result panel ───────────────────────────────────────────────

export function CompareResultPanel({
  comparison,
  onClose,
}: {
  comparison: RunComparisonResponse;
  onClose: () => void;
}) {
  const sum = comparison.summary;
  // Runs that had different phases on (e.g. rules off) were scored over different checks: keep the numbers, but do not
  // colour them or word them as an improvement.
  const likeForLike = sum.like_for_like !== false;
  const baseDate = new Date(comparison.base_run.completed_at);
  const compareDate = new Date(comparison.compare_run.completed_at);

  const issuesByType = useMemo(() => {
    const groups: Record<
      "new" | "resolved" | "persistent",
      typeof comparison.issue_changes
    > = {
      new: [],
      resolved: [],
      persistent: [],
    };
    comparison.issue_changes.forEach((c) => groups[c.change_type].push(c));
    return groups;
  }, [comparison]);

  return (
    <div
      className="glass-surface rounded-xl border border-[var(--primary)]/30 overflow-hidden"
      data-testid="run-compare-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02] border-b border-white/10">
        <div>
          <h4 className="text-sm font-semibold text-[var(--foreground)]">
            Run comparison
          </h4>
          <p className="text-[11px] text-white/55 font-mono mt-0.5">
            {comparison.base_run.run_id} ({baseDate.toLocaleString()})
            <ArrowRight className="inline w-3 h-3 mx-1.5 text-white/40" />
            {comparison.compare_run.run_id} ({compareDate.toLocaleString()})
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close comparison"
          className="p-1 rounded-md text-white/55 hover:text-white hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!likeForLike && (
        <div
          role="note"
          data-testid="run-compare-caveat"
          className="px-4 py-2 text-xs text-amber-200 bg-amber-400/10 border-b border-amber-400/30"
        >
          {sum.comparison_caveat ||
            "Not like-for-like: the two runs had different options switched on, so scores and issue counts are not directly comparable."}
        </div>
      )}

      {/* Summary metrics */}
      {/* Round-then-decide so sub-0.05 deltas don't render as "+0.0" /
          "-0.0" with the wrong tone (April 2026 user-reported bug). */}
      {(() => null)()}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
        {(() => {
          const rounded = Math.round(sum.aggregate_score_delta * 10) / 10;
          const isZero = rounded === 0;
          const value = isZero
            ? "—"
            : `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}`;
          const tone = !likeForLike
            ? "neutral"
            : isZero
              ? "neutral"
              : rounded > 0
                ? "good"
                : "bad";
          return (
            <SummaryMetric label="Score Δ" value={value} tone={tone as any} />
          );
        })()}
        <SummaryMetric
          label={likeForLike ? "Resolved issues" : "No longer reported"}
          value={sum.resolved_issues}
          tone={likeForLike && sum.resolved_issues > 0 ? "good" : "neutral"}
        />
        <SummaryMetric
          label={likeForLike ? "New issues" : "Newly reported"}
          value={sum.new_issues}
          tone={!likeForLike ? "neutral" : sum.new_issues > 0 ? "bad" : "good"}
        />
        <SummaryMetric
          label="Schema changes"
          value={sum.schema_changes}
          tone="neutral"
        />
        <SummaryMetric
          label="Tables added"
          value={sum.tables_added}
          tone="neutral"
        />
        <SummaryMetric
          label="Tables removed"
          value={sum.tables_removed}
          tone="neutral"
        />
      </div>

      {/* Per-table score deltas */}
      {comparison.table_score_deltas.length > 0 && (
        <DiffSection title="Per-table score changes">
          <table className="w-full text-xs">
            <thead className="text-[11px] uppercase tracking-wider text-white/45">
              <tr>
                <th className="text-left px-3 py-1.5">Table</th>
                <th className="text-right px-3 py-1.5">Old</th>
                <th className="text-right px-3 py-1.5">New</th>
                <th className="text-right px-3 py-1.5">Δ</th>
              </tr>
            </thead>
            <tbody>
              {comparison.table_score_deltas.map((d) => {
                // April 2026 fix: previously rendered "+0.0" / "-0.0"
                // for sub-0.05 changes — rounding artefact. Now we round
                // to 1 decimal first, then pick the sign + colour from
                // the ROUNDED value. Anything that rounds to zero shows
                // as "—" (no meaningful change) in neutral white.
                const rounded = Math.round(d.delta * 10) / 10;
                const isZero = rounded === 0;
                const display = isZero
                  ? "—"
                  : `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}`;
                const colorCls = isZero
                  ? "text-white/65"
                  : rounded > 0
                    ? "text-emerald-300"
                    : "text-red-300";
                return (
                  <tr key={d.table} className="border-t border-white/5">
                    <td className="px-3 py-1.5 font-mono text-white/85">
                      {d.table}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-white/65">
                      {d.old_score.toFixed(0)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-white/85">
                      {d.new_score.toFixed(0)}
                    </td>
                    <td
                      className={`px-3 py-1.5 text-right tabular-nums font-semibold ${colorCls}`}
                    >
                      {display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DiffSection>
      )}

      {/* Issue changes — grouped */}
      {(["resolved", "new", "persistent"] as const).map((kind) => {
        const list = issuesByType[kind];
        if (!list.length) return null;
        const tone = !likeForLike
          ? "text-white/70"
          : kind === "resolved"
            ? "text-emerald-300"
            : kind === "new"
              ? "text-red-300"
              : "text-amber-200";
        return (
          <DiffSection
            key={kind}
            title={`${
              !likeForLike && kind === "resolved"
                ? "No longer reported"
                : !likeForLike && kind === "new"
                  ? "Newly reported"
                  : `${kind[0].toUpperCase() + kind.slice(1)} issues`
            } (${list.length})`}
          >
            <ul className="text-xs divide-y divide-white/5">
              {list.slice(0, 50).map((c, idx) => (
                <li
                  key={`${c.table}-${idx}`}
                  className="px-3 py-1.5 flex gap-2"
                >
                  <span className={`shrink-0 ${tone} font-mono w-20`}>
                    {c.table}
                  </span>
                  <span className="text-white/80">{c.issue}</span>
                  {c.severity && (
                    <span className="ml-auto text-[11px] uppercase tracking-wider text-white/40">
                      {c.severity}
                    </span>
                  )}
                </li>
              ))}
              {list.length > 50 && (
                <li className="px-3 py-1.5 text-white/40 italic">
                  + {list.length - 50} more
                </li>
              )}
            </ul>
          </DiffSection>
        );
      })}

      {/* Schema changes */}
      {comparison.schema_changes.length > 0 && (
        <DiffSection
          title={`Schema changes (${comparison.schema_changes.length})`}
        >
          <ul className="text-xs divide-y divide-white/5">
            {comparison.schema_changes.map((c, i) => (
              <li
                key={`${c.table}-${c.column}-${i}`}
                className="px-3 py-1.5 flex gap-2 items-baseline"
              >
                <span className="font-mono w-32 text-white/65 shrink-0">
                  {c.table}.{c.column}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/40 w-28 shrink-0">
                  {c.change_type.replace("_", " ")}
                </span>
                {(c.old_value || c.new_value) && (
                  <span className="text-white/70 font-mono text-[11px]">
                    {c.old_value || "∅"} → {c.new_value || "∅"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </DiffSection>
      )}

      {/* Tables added/removed */}
      {(comparison.tables_added.length > 0 ||
        comparison.tables_removed.length > 0) && (
        <DiffSection title="Table inventory changes">
          <div className="px-3 py-2 text-xs space-y-1.5">
            {comparison.tables_added.length > 0 && (
              <div>
                <span className="text-emerald-300 mr-2">+ Added:</span>
                <span className="font-mono text-white/80">
                  {comparison.tables_added.join(", ")}
                </span>
              </div>
            )}
            {comparison.tables_removed.length > 0 && (
              <div>
                <span className="text-red-300 mr-2">− Removed:</span>
                <span className="font-mono text-white/80">
                  {comparison.tables_removed.join(", ")}
                </span>
              </div>
            )}
          </div>
        </DiffSection>
      )}

      {!sum.has_changes && (
        <div className="px-4 py-6 text-center text-sm text-white/55">
          The two runs are identical — no DQ score change, no schema drift, no
          issue movement.
        </div>
      )}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "good" | "bad" | "neutral";
}) {
  const color =
    tone === "good"
      ? "text-emerald-300"
      : tone === "bad"
        ? "text-red-300"
        : "text-white/85";
  return (
    <div className="rounded-md bg-white/[0.02] border border-white/5 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-white/45">
        {label}
      </div>
      <div className={`text-lg font-semibold tabular-nums ${color}`}>
        {value}
      </div>
    </div>
  );
}

function DiffSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-white/10">
      <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-white/55 bg-white/[0.02]">
        {title}
      </div>
      {children}
    </div>
  );
}
